import { Text } from '@thanx/uikit/text'
import palette from 'constants/palette'
import { buildTranslate } from 'locales'
import React from 'react'
import { useStyletron } from 'styletron-react'
import { merchantTheme } from 'theme'
import SectionShell from './SectionShell'

const t = buildTranslate('recovery_ai.promo.losing_guests')

// Figma 129:1756.
const SECTION_PADDING = '48px 86px'
const SECTION_GAP = '72px'
const INFO_GAP = '24px'
const TITLE_LINE_HEIGHT = '40px'
const BODY_LINE_HEIGHT = '24px'

const CARD_WIDTH = '500px'
const CARD_PADDING = '18px 36px'
const CARD_GAP = '16px'
const CARD_BORDER_RADIUS = '16px'
const CARD_TITLE_LINE_HEIGHT = '28px'
// Figma "Elevation/3": drop shadow #36424E at 20% alpha, y-offset 8, blur 24.
const CARD_SHADOW = '0px 8px 24px 0px rgba(54, 66, 78, 0.2)'

const ROWS_GAP = '16px'
const ROW_GAP = '16px'
const ROW_HEIGHT = '48px'
const ROW_LABEL_WIDTH = '160px'
const ROW_VALUE_WIDTH = '80px'
const BAR_HEIGHT = '20px'
const BAR_BORDER_RADIUS = '4px'
const WITH_LABEL_LINE_HEIGHT = '24px'
const WITHOUT_LABEL_LINE_HEIGHT = '20px'
const WITH_VALUE_LINE_HEIGHT = '40px'
const WITHOUT_VALUE_LINE_HEIGHT = '28px'

// Figma draws each fill as a fixed-width rectangle clipped by the bar track. The
// track is 192px wide there (card 536 − 72 horizontal padding − 160 label − 80
// value − 2 × 16 gap), so the 264px "with" fill clips to the whole track and the
// 164px "without" fill covers 164/192 ≈ 85% of it. Held as percentages so the
// designed proportion survives a card that is not exactly 536px wide.
const WITH_RECOVERY_BAR_WIDTH = '100%'
const WITHOUT_RECOVERY_BAR_WIDTH = '85%'

const LosingGuests = () => {
  const [css] = useStyletron()

  const rowClass = css({
    alignItems: 'center',
    display: 'flex',
    gap: ROW_GAP,
    height: ROW_HEIGHT,
    width: '100%',
  })
  const withLabelClass = css({
    flexShrink: 0,
    lineHeight: WITH_LABEL_LINE_HEIGHT,
    width: ROW_LABEL_WIDTH,
  })
  const withoutLabelClass = css({
    flexShrink: 0,
    lineHeight: WITHOUT_LABEL_LINE_HEIGHT,
    width: ROW_LABEL_WIDTH,
  })
  const barTrackClass = css({
    backgroundColor: merchantTheme.colors.grey05,
    borderRadius: BAR_BORDER_RADIUS,
    flex: '1 0 0',
    height: BAR_HEIGHT,
    minWidth: 0,
    overflow: 'hidden',
    position: 'relative',
  })

  return (
    <SectionShell
      backgroundColor={merchantTheme.colors.grey05}
      padding={SECTION_PADDING}
    >
      <div
        className={css({
          alignItems: 'center',
          display: 'flex',
          gap: SECTION_GAP,
        })}
      >
        <div
          className={css({
            display: 'flex',
            flex: '1 0 0',
            flexDirection: 'column',
            gap: INFO_GAP,
            minWidth: 0,
          })}
        >
          <Text.Header2
            tag="h2"
            color="grey90"
            className={css({ lineHeight: TITLE_LINE_HEIGHT, margin: 0 })}
          >
            {t('title')}
          </Text.Header2>
          <Text.BodyText3
            tag="p"
            color="grey70"
            className={css({ lineHeight: BODY_LINE_HEIGHT, margin: 0 })}
          >
            {t('subtitle')}
          </Text.BodyText3>
        </div>

        <div
          className={css({
            backgroundColor: palette.white,
            border: `1px solid ${palette.grey20}`,
            borderRadius: CARD_BORDER_RADIUS,
            boxShadow: CARD_SHADOW,
            display: 'flex',
            flexDirection: 'column',
            flexShrink: 0,
            gap: CARD_GAP,
            padding: CARD_PADDING,
            width: CARD_WIDTH,
          })}
        >
          <Text.Header3
            tag="h3"
            color="grey90"
            className={css({ lineHeight: CARD_TITLE_LINE_HEIGHT, margin: 0 })}
          >
            {t('widget.title')}
          </Text.Header3>

          <div
            className={css({
              display: 'flex',
              flexDirection: 'column',
              gap: ROWS_GAP,
              width: '100%',
            })}
          >
            <div className={rowClass}>
              <Text.BodyText3
                bold
                color={palette.nephritis}
                className={withLabelClass}
              >
                {t('widget.with_recovery')}
              </Text.BodyText3>
              {/* The bars restate the adjacent percentages, so they are hidden
                from assistive tech rather than duplicated as text. */}
              <div className={barTrackClass} aria-hidden="true">
                <div
                  className={css({
                    backgroundColor: palette.nephritis,
                    borderRadius: BAR_BORDER_RADIUS,
                    bottom: 0,
                    left: 0,
                    position: 'absolute',
                    top: 0,
                    width: WITH_RECOVERY_BAR_WIDTH,
                  })}
                />
              </div>
              <Text.Header2
                color={palette.nephritis}
                className={css({
                  lineHeight: WITH_VALUE_LINE_HEIGHT,
                  textAlign: 'right',
                  width: ROW_VALUE_WIDTH,
                })}
              >
                {t('widget.with_value')}
              </Text.Header2>
            </div>

            <div className={rowClass}>
              <Text.BodyText4 bold color="grey70" className={withoutLabelClass}>
                {t('widget.without_recovery')}
              </Text.BodyText4>
              <div className={barTrackClass} aria-hidden="true">
                <div
                  className={css({
                    backgroundColor: palette.grey30,
                    borderRadius: BAR_BORDER_RADIUS,
                    bottom: 0,
                    left: 0,
                    position: 'absolute',
                    top: 0,
                    width: WITHOUT_RECOVERY_BAR_WIDTH,
                  })}
                />
              </div>
              <Text.Header3
                color="grey70"
                className={css({
                  lineHeight: WITHOUT_VALUE_LINE_HEIGHT,
                  textAlign: 'right',
                  width: ROW_VALUE_WIDTH,
                })}
              >
                {t('widget.without_value')}
              </Text.Header3>
            </div>
          </div>
        </div>
      </div>
    </SectionShell>
  )
}

export default LosingGuests
